import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { parseAdobeCookie } from '@thd-olt-functional/utils/src/adobe/adobe';
import ExperienceContext from './ExperienceContext';

const ExperienceProvider = ({ value, children, disableLocalization = false }) => {
  const isServer = !((
    typeof window !== 'undefined'
    && window.document
    && window.document.createElement));

  const [store, setStore] = useState(null);
  const [nearByStores, setNearByStores] = useState([]);
  const [adobeCookie, setAdobeCookie] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [nativeFlags, setNativeFlags] = useState({});

  const cntx = {
    isServer,
    cookie: value.cookie,
    channel: value.channel,
    configService: value.configService,
    hosts: value.hosts,
    path: value.path,
    store: value.store,
    clientStore: value.clientStore,
    segment: value.segment,
    dataType: value.dataType,
    isDebugMode: typeof value.isDebugMode !== 'undefined'
      ? value.isDebugMode
      : false,
    isLocal: value.isLocal,
    customer: value.customer,
    localStoreZip: value.localStoreZip,
    deliveryZip: value.deliveryZip,
    isConsumerApp: value.isConsumerApp,
    mounted,
    nativeFlags,
    nearByStores
  };

  const handleLocalization = (methods) => {
    const localStore = methods?.getLocalizedStore();
    if (localStore) {
      setStore({
        storeHours: localStore.storeHours,
        storeId: localStore.storeId,
        storeName: localStore.name,
        storeZip: localStore.zipcode
      });
    }
    return methods;
  };
  useEffect(() => {
    const localize = typeof THD_LOCALIZER_AUTO_INIT !== 'undefined' && THD_LOCALIZER_AUTO_INIT?.Localizer?.localize;// eslint-disable-line
    if (localize && !disableLocalization) {
      localize()
        .then(handleLocalization);
    }
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('NATIVE_APP.STORE_INFO', ({ output }) => {
      const { storeID, nearByStores: nativeNearByStores } = output || {};
      if (storeID !== value.store.storeId) {
        if (localize && !disableLocalization && storeID) {
          localize({ storeId: storeID })
            .then(handleLocalization)
            .then(() => {
              window.location.reload();
            });
        }
      }
      if (nativeNearByStores) {
        setNearByStores(nativeNearByStores);
      }
    });
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('NEARBY_STORES.update', ({ output }) => {
      const { nearByStores: updatedNearByStores } = output || {};
      if (updatedNearByStores) {
        setNearByStores(updatedNearByStores);
      }
    });
    if (!isServer && typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('experience-context.ready', cntx);
    }
  }, []);

  useEffect(() => {
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find((row) => row.startsWith('AMCV_F6421253512D2C100A490D45%40AdobeOrg'))
        .split('=')[1];

      const ADOBE_COOKIE = parseAdobeCookie(cookieValue);
      setAdobeCookie(ADOBE_COOKIE);
    } catch (error) {
      setAdobeCookie({});
    }
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (value?.isConsumerApp) {
      window.LIFE_CYCLE_EVENT_BUS.off('setNativeAppFlags');
      window.LIFE_CYCLE_EVENT_BUS.on('setNativeAppFlags', ({ output = {} }) => {
        setNativeFlags({
          ...nativeFlags,
          ...output.nativeFlags
        });
      });
    }
  }, [value?.isConsumerApp]);

  if (store) {
    cntx.clientStore = store;
  }
  if (nearByStores) {
    cntx.nearByStores = nearByStores;
  }
  if (adobeCookie) {
    cntx.cookie.adobeCookie = adobeCookie;
  }

  return (
    <ExperienceContext.Provider value={cntx}>
      {children}
    </ExperienceContext.Provider>
  );
};

ExperienceProvider.propTypes = {
  value: PropTypes.shape([
    PropTypes.any,
    PropTypes.node
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  disableLocalization: PropTypes.bool
};

ExperienceProvider.defaultProps = {
  value: null,
  children: null,
  disableLocalization: false
};

export default ExperienceProvider;
